import {ChangeDetectionStrategy, Component, inject, signal} from '@angular/core';
import {Button} from "primeng/button";
import {MessageService, PrimeTemplate} from "primeng/api";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {TableModule} from "primeng/table";
import {Tag} from "primeng/tag";
import {EmailService} from "../../../services/email.service";
import {AsyncPipe, DatePipe} from "@angular/common";
import {finalize} from "rxjs";
import {EmailForwardingService} from "../../../services/email-forwarding.service";
import {EmailDto} from "admin-api-dto";

@Component({
  selector: 'app-emails-tab',
  standalone: true,
  imports: [
    Button,
    PrimeTemplate,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    Tag,
    AsyncPipe,
    DatePipe
  ],
  templateUrl: './emails-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailsTabComponent {
  protected readonly emails$ = this.emailService.emails$;
  protected readonly loading$ = this.emailService.loading$;
  protected readonly hasNextPage$ = this.emailService.hasNextPage$;
  protected readonly hasPreviousPage$ = this.emailService.hasPreviousPage$;
  protected readonly retryingEmailKey = signal<string | undefined>(undefined);
  protected readonly forwardingEnabled = signal<boolean | undefined>(undefined);
  protected readonly forwardingLoading = signal(false);
  protected sinceDate = '';
  protected readonly sinceEmails = signal<EmailDto[] | undefined>(undefined);
  protected readonly sinceLoading = signal(false);

  private readonly messageService = inject(MessageService);
  private readonly emailForwardingService = inject(EmailForwardingService);

  constructor(private readonly emailService: EmailService) {
    this.emailService.initialize();
    this.loadForwardingState();
  }

  private loadForwardingState() {
    this.emailForwardingService.getState().subscribe({
      next: ({enabled}) => this.forwardingEnabled.set(enabled),
      error: () => this.messageService.add({severity: 'error', summary: 'Failed to load email forwarding state'})
    });
  }

  protected toggleForwarding() {
    const current = this.forwardingEnabled();
    if (current === undefined || this.forwardingLoading()) return;
    this.forwardingLoading.set(true);
    this.emailForwardingService.setState(!current).pipe(
      finalize(() => this.forwardingLoading.set(false))
    ).subscribe({
      next: () => {
        this.forwardingEnabled.set(!current);
        this.messageService.add({
          severity: 'success',
          summary: `Email forwarding ${!current ? 'enabled' : 'disabled'}`
        });
      },
      error: () => this.messageService.add({severity: 'error', summary: 'Failed to update email forwarding state'})
    });
  }

  protected fetchSince() {
    if (!this.sinceDate || this.sinceLoading()) return;
    this.sinceLoading.set(true);
    this.emailService.getEmailsSince(new Date(this.sinceDate).toISOString()).pipe(
      finalize(() => this.sinceLoading.set(false))
    ).subscribe({
      next: emails => this.sinceEmails.set(emails.sort((a, b) => b.timestamp.localeCompare(a.timestamp))),
      error: () => this.messageService.add({severity: 'error', summary: 'Failed to fetch emails'})
    });
  }

  public downloadEmail(s3Key: string) {
    this.emailService.openEmail(s3Key);
  }

  public nextPage() {
    void this.emailService.nextPage();
  }

  public previousPage() {
    void this.emailService.previousPage();
  }

  public retryEmail(s3Key: string, recipient: string) {
    this.retryingEmailKey.set(s3Key);
    this.emailService.retryEmail(s3Key, recipient).pipe(
      finalize(() => this.retryingEmailKey.set(undefined))
    ).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Email retried',
          detail: `Email with key ${s3Key} retried successfully`
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to retry email',
          detail: error?.error?.message ?? 'Unable to retry email.'
        });
      }
    });
  }
}
